import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  LiveRatesPe,
  PeruRail,
  PERU_RAILS,
  PeruService,
  VesPenConfig,
  VesPenLiveRates,
  VesPenPreview,
  VesPenPreviewItem,
  VesPenTasaSource,
} from '../../services/peru.service';

/** Form de la pata VES→USD de Perú (singleton pais='PE'; la pata USD→PEN se configura aparte). */
interface VesForm {
  activo: boolean;
  tasaSource: VesPenTasaSource;
  tasaManual: number | null;
  tasaManualVigenteHasta: string | null;
  markupPct: number | null;
  minVes: number | null;
  motivo: string;
}

const DEFAULT_FORM: VesForm = {
  activo: false,
  tasaSource: 'AUTO',
  tasaManual: null,
  tasaManualVigenteHasta: null,
  markupPct: 0,
  minVes: 0,
  motivo: '',
};

/** Mínimo del rail (USD), igual que USD→PEN. */
const MIN_USD = 20;
/** Montos VES de la tabla de preview (≈ 20 / 65 / 130 / 1300 USD a ~40). */
const PREVIEW_MONTOS_VES = [800, 2600, 5200, 52000];

export interface RailVesPenConfigData {
  countryCode?: string; // 'PE'
  countryName?: string;
}

/**
 * MODAL "VES → PEN — Configuración" (pata VES→USD). Config PROPIA e
 * INDEPENDIENTE de VES→COP (`ves_cop_config` con `pais='PE'`). El usuario
 * venezolano paga con su saldo VES y el beneficiario en PE recibe PEN — ve UNA
 * sola tasa PEN/VES. Acá se gobierna solo la conversión VES→USD (fuente
 * AUTO=`ves_usdt_rates`/MANUAL + markup + activación); la pata USD→PEN (Vita,
 * ESPEJO) se configura en "USD/PEN". Espejo de `RailVesCopConfigComponent`.
 */
@Component({
  selector: 'app-rail-ves-pen-config',
  templateUrl: './rail-ves-pen-config.component.html',
  styleUrls: ['./rail-ves-pen-config.component.sass'],
})
export class RailVesPenConfigComponent implements OnInit, OnDestroy {
  readonly rails: PeruRail[] = PERU_RAILS;
  readonly railLabel: Record<PeruRail, string> = {
    PE: 'Interbancario (CCI · instantáneo)',
  };
  readonly sources: { value: VesPenTasaSource; label: string; hint: string }[] = [
    { value: 'AUTO', label: 'Tasa Aguacate (vivo)', hint: 'ves_usdt_rates.tasa_final (ya trae ~1%)' },
    { value: 'MANUAL', label: 'Manual (override)', hint: 'Tasa fija con vencimiento' },
  ];

  config: VesPenConfig | null = null;
  live: VesPenLiveRates | null = null;
  /** Tasas vivas de la pata USD→PEN (Vita) — para mostrar la tasa Vita actual. */
  usdLive: LiveRatesPe | null = null;

  form: VesForm = { ...DEFAULT_FORM };

  preview: VesPenPreview | null = null;
  previewLoading = false;
  private previewTimer: any = null;
  private previewSeq = 0;

  /** USD→PEN (master). Si OFF, no se puede ofrecer VES→PEN (va por ese puente). */
  usdtPenOfrecido = true;
  loading = false;
  saving = false;
  error: string | null = null;
  okMsg: string | null = null;

  constructor(
    private svc: PeruService,
    public dialogRef: MatDialogRef<RailVesPenConfigComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RailVesPenConfigData,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    if (this.previewTimer) clearTimeout(this.previewTimer);
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const [config, live, usdLive, railes] = await Promise.all([
        this.svc.getVesConfig(),
        this.svc.getVesLive(),
        this.svc.getLive().catch(() => null),
        this.svc.getRailesActivos().catch(() => null),
      ]);
      this.config = config;
      this.live = live;
      this.usdLive = usdLive;
      this.fromConfig(config);
      // El on/off vive en el switchboard (parametros), no en la config de precio.
      this.form.activo = !!railes?.pen?.ves_pen;
      // usdt_pen es el MASTER de PEN: VES→PEN va por el puente USD→PEN, así que
      // no se puede ofrecer VES→PEN si USD→PEN está apagado.
      this.usdtPenOfrecido = railes?.pen?.usdt_pen !== false;
      if (!this.usdtPenOfrecido) this.form.activo = false;
      this.refreshPreview(true);
    } catch (e: any) {
      this.error = e?.error?.message || e?.message || 'No se pudo cargar la configuración';
    } finally {
      this.loading = false;
    }
  }

  private fromConfig(c: VesPenConfig | null): void {
    if (!c) {
      this.form = { ...DEFAULT_FORM };
      return;
    }
    this.form = {
      activo: false, // lo setea load() desde el switchboard
      tasaSource: c.tasaSource,
      tasaManual: c.tasaManual != null ? Number(c.tasaManual) : null,
      tasaManualVigenteHasta: c.tasaManualVigenteHasta
        ? c.tasaManualVigenteHasta.slice(0, 16)
        : null,
      markupPct: c.markupPct != null ? Number(c.markupPct) : 0,
      minVes: c.minVes != null ? Number(c.minVes) : 0,
      motivo: '',
    };
  }

  onFormChange(): void {
    this.okMsg = null;
    this.refreshPreview();
  }

  /** Toggle "Ofrecer VES→PEN". Bloqueado si USD→PEN (master) está OFF. */
  toggleActivo(): void {
    if (!this.usdtPenOfrecido) return;
    this.form.activo = !this.form.activo;
    this.onFormChange();
  }

  refreshPreview(inmediato = false): void {
    if (this.previewTimer) clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => this.fetchPreview(), inmediato ? 0 : 600);
  }

  private async fetchPreview(): Promise<void> {
    const seq = ++this.previewSeq;
    this.previewLoading = true;
    try {
      const p = await this.svc.vesQuotePreview(PREVIEW_MONTOS_VES, {
        tasaSource: this.form.tasaSource,
        tasaManual:
          this.form.tasaSource === 'MANUAL' && this.form.tasaManual != null
            ? Number(this.form.tasaManual)
            : undefined,
        tasaManualVigenteHasta:
          this.form.tasaSource === 'MANUAL' && this.form.tasaManualVigenteHasta
            ? new Date(this.form.tasaManualVigenteHasta).toISOString()
            : undefined,
        markupPct: this.form.markupPct != null ? Number(this.form.markupPct) : undefined,
        minVes: this.form.minVes != null ? Number(this.form.minVes) : undefined,
      });
      if (seq === this.previewSeq) this.preview = p;
    } catch {
      if (seq === this.previewSeq) this.preview = null;
    } finally {
      if (seq === this.previewSeq) this.previewLoading = false;
    }
  }

  async save(): Promise<void> {
    if (this.form.tasaSource === 'MANUAL') {
      if (!(Number(this.form.tasaManual) > 0) || !this.form.tasaManualVigenteHasta) {
        this.error = 'MANUAL requiere tasa y vencimiento futuros.';
        return;
      }
    }
    this.saving = true;
    this.error = null;
    this.okMsg = null;
    try {
      // 1) On/off del rail (switchboard en parametros; merge preserva usdt_pen).
      await this.svc.setRailesActivos({ vesPen: !!this.form.activo });
      // 2) Config de PRECIO (entity versionada pais='PE') — sin el on/off.
      await this.svc.upsertVesConfig({
        tasaSource: this.form.tasaSource,
        tasaManual:
          this.form.tasaSource === 'MANUAL' ? Number(this.form.tasaManual) : undefined,
        tasaManualVigenteHasta:
          this.form.tasaSource === 'MANUAL' && this.form.tasaManualVigenteHasta
            ? new Date(this.form.tasaManualVigenteHasta).toISOString()
            : undefined,
        markupPct: this.form.markupPct != null ? Number(this.form.markupPct) : undefined,
        minVes: this.form.minVes != null ? Number(this.form.minVes) : undefined,
        motivo: this.form.motivo || undefined,
      });
      this.okMsg = `Configuración guardada (${this.form.activo ? 'VES→PEN activo' : 'VES→PEN inactivo'}).`;
      await this.load();
    } catch (e: any) {
      this.error = e?.error?.message || e?.message || 'No se pudo guardar';
      await this.load();
    } finally {
      this.saving = false;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  // ── Helpers de vista ──

  itemsDe(rail: PeruRail): VesPenPreviewItem[] {
    return this.preview?.rails?.[rail] ?? [];
  }

  /** Tasa VES/USDT que se está usando (de la fuente activa + markup). */
  get tasaVesUsd(): number | null {
    return this.preview?.rate?.efectiva ?? this.live?.rate?.efectiva ?? null;
  }
  get tasaStale(): boolean {
    return !!(this.preview?.rate?.stale ?? this.live?.rate?.stale);
  }
  get sourceResuelta(): VesPenTasaSource | null {
    return this.preview?.rate?.source ?? this.live?.rate?.source ?? null;
  }

  /** Mínimo (20 USD) expresado en VES a la tasa actual. */
  get minVesEquivalente(): number | null {
    const r = this.tasaVesUsd;
    return r && r > 0 ? Math.ceil(MIN_USD * r) : null;
  }

  // ── Tasa Vita actual (pata de salida USD→PEN) ──
  vitaRetailPen(rail: PeruRail): number | null {
    return this.usdLive?.rails?.[rail]?.publicSellUsd ?? this.usdLive?.rails?.[rail]?.publicUsdSell ?? null;
  }
  vitaPartnerPen(rail: PeruRail): number | null {
    return this.usdLive?.rails?.[rail]?.partnerUsdSell ?? null;
  }

  private allItems(): VesPenPreviewItem[] {
    return this.rails.flatMap((r) => this.itemsDe(r));
  }

  // ── Alertas (espejo de las del modal VES→COP) ──

  get alertSinMargen(): boolean {
    return this.allItems().some((i) => i.cumpleMinimo && i.disponible && !i.margenOk);
  }
  get alertBajoVita(): boolean {
    return this.allItems().some(
      (i) => i.clamped || (i.ofertaVitaPen != null && i.recibePen < i.ofertaVitaPen),
    );
  }
  get alertEspejoDegradado(): boolean {
    return this.allItems().some((i) => i.espejoFallback);
  }
  get alertFeedStale(): boolean {
    return this.tasaStale || this.allItems().some((i) => i.stale);
  }
}
